import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Save, RotateCcw, UndoDotIcon } from "lucide-react";
import toast from "react-hot-toast";
import { ConfigSection } from "./configSection";
import config from "../config.json";
import axios from "axios";

const initialConfig = {
  welcome_page_description: {
    original:
      "Discover what's preventing your website's growth and unlock hidden opportunities to maximize revenue potential! Identify and fix key issues to accelerate your website's success.",
    edited: null,
    max_length: 200,
  },
  generated_by: {
    company: {
      original: "GetWebsite.Report",
      edited: null,
    },
    company_logo: {
      original: "GWR logo",
      edited: null,
    },
    website: {
      original: "https://getwebsite.report",
      edited: null,
    },
    name: {
      original: "Josh Spectre",
      edited: null,
    },
    email: {
      original: "josh@getwebsite.report",
      edited: null,
    },
    person_image: {
      original: "",
      edited: null,
    },
    phone_number: {
      original: "+1 1234567890",
      edited: null,
    },
  },
  audit_intro: {
    original:
      "<p>Your website plays a crucial role in your business success, but is it working for you? This report provides a comprehensive assessment of your website's strengths and weaknesses across more than 70 key areas. With detailed yet easy-to-understand recommendations, you'll gain valuable insights to optimize your online presence. Stop guessing—use this structured, jargon-free report to prioritize and implement impactful improvements.</p>",
    edited: null,
    max_length: 900,
  },
  audit_closure: {
    original:
      "<p>Thank you for reviewing this audit. Your website's design, content, and optimization influence not only your search rankings but also your ability to convert visitors into customers. Implementing the actionable insights in this report will help you outrank competitors, increase traffic, and generate more leads. If you're ready to elevate your website's performance, let's talk. Schedule a free consultation to discover proven strategies that can transform your website into a powerful marketing tool.</p>",
    edited: null,
    max_length: 700,
  },
  book_appointment: {
    title: {
      original: "SUPERCHARGE YOUR WEBSITE LEADS!",
      edited: null,
      max_length: 100,
    },
    body: {
      original:
        "We can help you turn your website into a lead-generating powerhouse. Our proven system and strategies have helped businesses optimize their digital presence and maximize conversions—all within weeks and at a fraction of the usual cost.",
      edited: null,
      max_length: 300,
    },
    button_text: {
      original: "Schedule my Website Consultation",
      edited: null,
      max_length: 50,
    },
    button_link: {
      original: "https://getwebsite.report",
      edited: null,
    },
  },
};

const getCustomisation = async () => {
  try {
    const response = await axios.get(
      `${config.apiBaseUrl}/users/customization`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching customisation data:", error);
  }
};

const saveCustomisation = async (user_id, custom_data) => {
  try {
    const response = await axios.post(
      `${config.apiBaseUrl}/users/customization`,
      {
        user_id,
        custom_data,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error saving customisation data:", error);
  }
};

const CustomiseReport = ({ showPopUp = false, setShowPopUp }) => {
  const [customisation, setCustomisation] = useState(initialConfig);
  const [hasChanges, setHasChanges] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isSaving, setSaving] = useState(false);

  useEffect(() => {
    getCustomisation().then((data) => {
      setCustomisation(data.customization.custom_data || initialConfig);
      setUserId(data.customization.user_id || null);
    });
  }, []);

  useEffect(() => {
    const checkChanges = (obj) => {
      for (const key in obj) {
        if (obj[key] && typeof obj[key] === "object") {
          if (checkChanges(obj[key])) {
            return true;
          }
        } else if (
          key === "edited" &&
          obj[key] !== null &&
          obj[key] !== obj.original
        ) {
          return true;
        }
      }
      return false;
    };

    setHasChanges(checkChanges(customisation));
  }, [customisation]);

  const updateField = (path, value) => {
    setCustomisation((prev) => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      let current = newConfig;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      current[path[path.length - 1]].edited = value;
      return newConfig;
    });
  };

  const undoField = (path) => {
    setCustomisation((prev) => {
      const newConfig = JSON.parse(JSON.stringify(prev));
      let current = newConfig;
      for (let i = 0; i < path.length - 1; i++) {
        current = current[path[i]];
      }
      current[path[path.length - 1]].edited = null;
      return newConfig;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    saveCustomisation(userId, customisation).then((response) => {
      if (response) {
        setCustomisation(response.customization.custom_data);
        setSaving(false);
        toast.success("Configuration saved successfully!");
      } else {
        setSaving(false);
        toast.error("Failed to save configuration");
      }
    });
  };

  const useDefaults = () => {
    if (window.confirm("Are you sure you want to use default settings?")) {
      const updatedCustomisation = JSON.parse(JSON.stringify(customisation));
      const fillOriginalValues = (obj, initialObj) => {
        for (const key in obj) {
          if (obj[key] && typeof obj[key] === "object") {
            fillOriginalValues(obj[key], initialObj[key]);
          } else if (key === "edited") {
            obj[key] = initialObj["original"];
          }
        }
      };
      fillOriginalValues(updatedCustomisation, initialConfig);
      setCustomisation(updatedCustomisation);
    }
  };

  const resetChanges = () => {
    if (window.confirm("Are you sure you want to reset changes?")) {
      const updatedCustomisation = JSON.parse(JSON.stringify(customisation));
      const resetEditedValues = (obj) => {
        for (const key in obj) {
          if (obj[key] && typeof obj[key] === "object") {
            resetEditedValues(obj[key]);
          } else if (key === "edited") {
            obj[key] = null;
          }
        }
      };
      resetEditedValues(updatedCustomisation);
      setCustomisation(updatedCustomisation);
    }
  };

  return (
    <Dialog maxWidth="xl" open={showPopUp} onClose={() => setShowPopUp(false)}>
      <DialogTitle
        sx={{
          fontSize: "2rem",
          fontWeight: "medium",
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "white",
        }}
      >
        Report Customisation
        <IconButton
          onClick={() => setShowPopUp(false)}
          sx={{ position: "absolute", top: 10, right: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4, px: 2 }}>
          <Box component="section">
            <Typography variant="h5" gutterBottom>
              Welcome Page
            </Typography>
            <ConfigSection
              title="Description"
              field={customisation.welcome_page_description}
              onChange={(value) =>
                updateField(["welcome_page_description"], value)
              }
              onUndo={() => undoField(["welcome_page_description"])}
              isMultiline
            />
          </Box>

          <Box component="section">
            <Typography variant="h5" gutterBottom>
              Generated By
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              {Object.entries(customisation.generated_by).map(
                ([key, value]) =>
                  key !== "company_logo" &&
                  key !== "person_image" && (
                    <ConfigSection
                      key={key}
                      title={key
                        .split("_")
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ")}
                      field={value}
                      onChange={(newValue) =>
                        updateField(["generated_by", key], newValue)
                      }
                      onUndo={() => undoField(["generated_by", key])}
                    />
                  )
              )}
            </Box>
          </Box>

          <Box component="section">
            <Typography variant="h5" gutterBottom>
              Audit Content
            </Typography>
            <ConfigSection
              title="Introduction"
              field={customisation.audit_intro}
              onChange={(value) => updateField(["audit_intro"], value)}
              onUndo={() => undoField(["audit_intro"])}
              isRichText
            />
            <ConfigSection
              title="Closure"
              field={customisation.audit_closure}
              onChange={(value) => updateField(["audit_closure"], value)}
              onUndo={() => undoField(["audit_closure"])}
              isRichText
            />
          </Box>

          <Box component="section">
            <Typography variant="h5" gutterBottom>
              Book Appointment Section
            </Typography>
            {Object.entries(customisation.book_appointment).map(
              ([key, value]) => (
                <ConfigSection
                  key={key}
                  title={key
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                  field={value}
                  onChange={(newValue) =>
                    updateField(["book_appointment", key], newValue)
                  }
                  onUndo={() => undoField(["book_appointment", key])}
                />
              )
            )}
          </Box>
        </Box>
      </Container>

      <DialogActions
        sx={{ position: "sticky", bottom: 0, bgcolor: "white", py: 2 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            startIcon={<UndoDotIcon />}
            sx={{ px: 3, py: 1.5, borderRadius: 28, textTransform: "none" }}
            onClick={useDefaults}
            disabled={isSaving}
          >
            Use Defaults
          </Button>
          <Button
            variant="contained"
            disabled={!hasChanges || isSaving}
            startIcon={<RotateCcw />}
            sx={{ px: 3, py: 1.5, borderRadius: 28, textTransform: "none" }}
            onClick={resetChanges}
          >
            Reset Changes
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            startIcon={<Save />}
            sx={{
              px: 3,
              py: 1.5,
              borderRadius: 28,
              textTransform: "none",
            }}
          >
            {isSaving ? "Saving..." : "Save Changes"}
            {isSaving && <CircularProgress size={24} color="inherit" />}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CustomiseReport;
